<script setup lang="ts">
import TrustpilotReview from '@/components/TrustpilotReview.vue'
import { formatDistance } from 'date-fns'
import { onMounted, ref } from 'vue'

interface TrustpilotSummary {
  id: string
  country: string
  displayName: string
  name: {
    referring: string[]
    identifying: string
  }
  score: { stars: number; trustScore: number }
  status: string
  websiteUrl: string
  numberOfReviews: {
    total: number
    usedForTrustScoreCalculation: number
    oneStar: number
    twoStars: number
    threeStars: number
    fourStars: number
    fiveStars: number
  }
  links: {
    href: string
    rel: string
    method: string
  }[]
  badFit: null
}

interface TrustpilotReviews {
  links: {
    href: string
    method: string
    rel: string
  }[]
  reviews: {
    links: {
      href: string
      method: string
      rel: string
    }[]
    id: string
    consumer: {
      links: {
        href: string
        method: string
        rel: string
      }[]
      id: string
      displayName: string
      displayLocation: null
      numberOfReviews: number
    }
    businessUnit: {
      links: {
        href: string
        method: string
        rel: string
      }[]
      id: string
      identifyingName: string
      displayName: string
    }
    location: null
    stars: number
    title: string
    text: string
    language: string
    createdAt: string
    experiencedAt: string
    updatedAt: string | null
    companyReply: {
      text: string
      createdAt: string
      updatedAt: string
    } | null
    isVerified: boolean
    source: string
    numberOfLikes: number
    status: string
    reportData: null
    complianceLabels: unknown[]
    countsTowardsTrustScore: true
    countsTowardsLocationTrustScore: null
    invitation: { businessUnitId: string }
    businessUnitHistory: unknown[]
    reviewVerificationLevel: string
  }[]
}

const summary = ref<TrustpilotSummary | null>(null)
const reviews = ref<TrustpilotReviews | null>(null)

onMounted(async () => {
  summary.value = await getSummary()
  reviews.value = await getReviews()
})

const formatDate = (dateString: string) => {
  return formatDistance(dateString, new Date(), {
    addSuffix: true,
  })
}

async function getReviews() {
  const qs = new URLSearchParams({
    perPage: '20',
    stars: '5',
    apikey: '0fzqrZ1mlBAqQ2L4XIrCsvxzg4l4YCDu',
  })
  const response = await fetch(
    `https://api.trustpilot.com/v1/business-units/5df75011e5b7f100012e201f/reviews?${qs.toString()}`,
  )
  const reviews = await response.json()
  return reviews
}

async function getSummary() {
  const qs = new URLSearchParams({
    apikey: '0fzqrZ1mlBAqQ2L4XIrCsvxzg4l4YCDu',
  })
  const res = await fetch(
    `https://api.trustpilot.com/v1/business-units/5df75011e5b7f100012e201f?${qs.toString()}`,
  )
  const data = await res.json()
  return data
}
</script>

<template>
  <div>
    <div class="TrustpilotHighlights__ReviewContainer">
      <template v-if="reviews">
        <TrustpilotReview
          v-for="review in reviews.reviews"
          :key="review.id"
          :stars="review.stars"
          :is-verified="review.isVerified"
          :title="review.title"
          :text="review.text"
          :name="review.consumer.displayName"
          :date="formatDate(review.createdAt)"
          :loading="false"
        ></TrustpilotReview>
      </template>
      <template v-else>
        <TrustpilotReview
          v-for="i in 4"
          :key="i"
          :stars="5"
          :is-verified="true"
          title="Would totally recommend!"
          text="I have been shopping at Bower since 2021 and have never been disappointed. The quality of the products is amazing and the customer service is top-notch."
          name="Happy Customer"
          date="today"
          :loading="true"
        ></TrustpilotReview>
      </template>
    </div>
    <div v-if="summary">
      <p>
        Rated {{ summary.score.trustScore }} / 5 based on
        {{ summary.numberOfReviews.total.toLocaleString('en-GB') }} reviews.
        Showing our most recent 5 star reviews.
      </p>
    </div>
  </div>
</template>

<style>
.TrustpilotHighlights__ReviewContainer {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  margin: 16px 0;
}
</style>
